export type SectionGroupId = "content" | "price" | "audience" | "terms";

export interface SectionGroup {
  readonly id: SectionGroupId;
  readonly label: string;
  readonly subLabelTh: string;
  /** Material icon ligature shown before the group label in the rail. */
  readonly icon: string;
  /** What an author fills in this group, and what is read-only here. */
  readonly hint: string;
  readonly hintTh: string;
}

export const SECTION_GROUPS: readonly SectionGroup[] = [
  {
    id: "content",
    label: "Content",
    subLabelTh: "เนื้อหา",
    icon: "edit_note",
    hint: "Fill in the name, description, key selling, badges and benefits customers read.",
    hintTh: "กรอกชื่อ คำอธิบาย จุดขาย ป้าย และสิทธิประโยชน์ที่ลูกค้าเห็น",
  },
  {
    id: "price",
    label: "Price & validity",
    subLabelTh: "ราคาและระยะเวลา",
    icon: "sell",
    hint: "Set validity and billing here. Price and tax come from the catalog.",
    hintTh: "ตั้งค่าระยะเวลาใช้งานและรอบบิลที่นี่ ราคาและภาษีมาจาก catalog",
  },
  {
    id: "audience",
    label: "Audience & ranking",
    subLabelTh: "กลุ่มเป้าหมายและอันดับ",
    icon: "leaderboard",
    hint: "Set who can buy it and how it ranks on the shelf.",
    hintTh: "กำหนดว่าใครซื้อได้ และจัดอันดับบนชั้นวางอย่างไร",
  },
  {
    id: "terms",
    label: "Terms",
    subLabelTh: "ข้อกำหนด",
    icon: "gavel",
    hint: "Pick or create the terms and conditions customers accept.",
    hintTh: "เลือกหรือสร้างข้อกำหนดและเงื่อนไขที่ลูกค้าต้องยอมรับ",
  },
];

export interface SectionNav {
  readonly id: string;
  readonly label: string;
  readonly group: SectionGroupId;
}

export const SECTIONS: readonly SectionNav[] = [
  { id: "section-ranking", label: "Ranking scores", group: "audience" },
  { id: "section-ussd", label: "USSD No.", group: "price" },
  { id: "section-name", label: "Product name", group: "content" },
  { id: "section-description", label: "Product description", group: "content" },
  { id: "section-keyselling", label: "Key Selling", group: "content" },
  { id: "section-badge", label: "Badge", group: "content" },
  { id: "section-badge-detail", label: "Badge (Detail)", group: "content" },
  { id: "section-badge-upsell", label: "Upsell pop up", group: "content" },
  { id: "section-shelf", label: "Shelf & Category", group: "audience" },
  { id: "section-price", label: "Price & currency", group: "price" },
  { id: "section-tax", label: "Tax", group: "price" },
  { id: "section-sof", label: "Source of Fund", group: "price" },
  { id: "section-validity", label: "Validity", group: "price" },
  // Availability sits next to Validity because both are time-shaped, and an
  // author comparing "how long the package lasts" against "when we can sell
  // it" should see them together. The two are easy to confuse, so the card
  // carries a hint that spells out the difference.
  { id: "section-availability", label: "Availability", group: "price" },
  { id: "section-billing", label: "Billing cycle", group: "price" },
  { id: "section-eligibility", label: "Eligibility", group: "audience" },
  // Price Classification (CONTEXT.md): every channel sees Segment; the other
  // three fields are gated inside the card to the myAIS edit scope.
  { id: "section-classification", label: "Classification", group: "audience" },
  // Voice Channel Setting is the voice channel's content, and the most
  // operational block of it (voicebot recordings, flags, a status string), so
  // it sits at the tail of Content just above the open-ended Service
  // enrichment list.
  //
  // The `id` stays `section-civr`: it is an anchor target, the key the
  // voice-scope gate matches on, and what the section tests assert. Renaming
  // it would be churn with breakage risk and nothing visible to show for it;
  // only the LABEL is customer-facing.
  { id: "section-civr", label: "Voice Channel Setting", group: "content" },
  // The Online Store counterpart of section-civr: gated to the Online Store
  // edit scope the way section-civr is gated to voice.
  { id: "section-online-store", label: "Online Store", group: "content" },
  { id: "section-services", label: "Service enrichment", group: "content" },
  // Price Agreement (CONTEXT.md): the T&C per price, on every channel. Last,
  // so the long T&C preview never pushes the editable fields down.
  { id: "section-agreement", label: "Terms & Conditions", group: "terms" },
];

export interface SectionGroupView extends SectionGroup {
  readonly sections: readonly SectionNav[];
}

export function groupSections(
  visible: readonly SectionNav[],
): readonly SectionGroupView[] {
  return SECTION_GROUPS.map((g) => ({
    ...g,
    sections: visible.filter((s) => s.group === g.id),
  })).filter((g) => g.sections.length > 0);
}

const GROUP_BY_SECTION: ReadonlyMap<string, SectionGroupId> = new Map(
  SECTIONS.map((s) => [s.id, s.group]),
);

export function groupOfSection(id: string | null): SectionGroupId | undefined {
  return id ? GROUP_BY_SECTION.get(id) : undefined;
}
